'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MolecularWeightCalculator() {
  const t = useTranslations("tools.molecularWeightCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [formula, setFormula] = useState('');
  const [selectedCompound, setSelectedCompound] = useState('');
  const [results, setResults] = useState<{
    totalWeight: number;
    composition: Array<{
      symbol: string;
      name: string;
      count: number;
      weight: number;
      percentage: string;
    }>;
  } | null>(null);
  const [error, setError] = useState('');

  // Atomic weights database
  const atomicWeights: Record<string, { weight: number; name: string }> = {
    'H': { weight: 1.008, name: 'Hydrogen' },
    'He': { weight: 4.0026, name: 'Helium' },
    'Li': { weight: 6.94, name: 'Lithium' },
    'Be': { weight: 9.0122, name: 'Beryllium' },
    'B': { weight: 10.81, name: 'Boron' },
    'C': { weight: 12.011, name: 'Carbon' },
    'N': { weight: 14.007, name: 'Nitrogen' },
    'O': { weight: 15.999, name: 'Oxygen' },
    'F': { weight: 18.998, name: 'Fluorine' },
    'Ne': { weight: 20.18, name: 'Neon' },
    'Na': { weight: 22.99, name: 'Sodium' },
    'Mg': { weight: 24.305, name: 'Magnesium' },
    'Al': { weight: 26.982, name: 'Aluminum' },
    'Si': { weight: 28.085, name: 'Silicon' },
    'P': { weight: 30.974, name: 'Phosphorus' },
    'S': { weight: 32.06, name: 'Sulfur' },
    'Cl': { weight: 35.45, name: 'Chlorine' },
    'Ar': { weight: 39.948, name: 'Argon' },
    'K': { weight: 39.098, name: 'Potassium' },
    'Ca': { weight: 40.078, name: 'Calcium' },
    'Sc': { weight: 44.956, name: 'Scandium' },
    'Ti': { weight: 47.867, name: 'Titanium' },
    'V': { weight: 50.942, name: 'Vanadium' },
    'Cr': { weight: 51.996, name: 'Chromium' },
    'Mn': { weight: 54.938, name: 'Manganese' },
    'Fe': { weight: 55.845, name: 'Iron' },
    'Co': { weight: 58.933, name: 'Cobalt' },
    'Ni': { weight: 58.693, name: 'Nickel' },
    'Cu': { weight: 63.546, name: 'Copper' },
    'Zn': { weight: 65.38, name: 'Zinc' },
    'Ga': { weight: 69.723, name: 'Gallium' },
    'Ge': { weight: 72.63, name: 'Germanium' },
    'As': { weight: 74.922, name: 'Arsenic' },
    'Se': { weight: 78.971, name: 'Selenium' },
    'Br': { weight: 79.904, name: 'Bromine' },
    'Kr': { weight: 83.798, name: 'Krypton' },
    'Rb': { weight: 85.468, name: 'Rubidium' },
    'Sr': { weight: 87.62, name: 'Strontium' },
    'Y': { weight: 88.906, name: 'Yttrium' },
    'Zr': { weight: 91.224, name: 'Zirconium' },
    'Nb': { weight: 92.906, name: 'Niobium' },
    'Mo': { weight: 95.95, name: 'Molybdenum' },
    'Tc': { weight: 98, name: 'Technetium' },
    'Ru': { weight: 101.07, name: 'Ruthenium' },
    'Rh': { weight: 102.91, name: 'Rhodium' },
    'Pd': { weight: 106.42, name: 'Palladium' },
    'Ag': { weight: 107.87, name: 'Silver' },
    'Cd': { weight: 112.41, name: 'Cadmium' },
    'In': { weight: 114.82, name: 'Indium' },
    'Sn': { weight: 118.71, name: 'Tin' },
    'Sb': { weight: 121.76, name: 'Antimony' },
    'Te': { weight: 127.6, name: 'Tellurium' },
    'I': { weight: 126.9, name: 'Iodine' },
    'Xe': { weight: 131.29, name: 'Xenon' },
    'Cs': { weight: 132.91, name: 'Cesium' },
    'Ba': { weight: 137.33, name: 'Barium' },
    'La': { weight: 138.91, name: 'Lanthanum' },
    'Ce': { weight: 140.12, name: 'Cerium' },
    'Pr': { weight: 140.91, name: 'Praseodymium' },
    'Nd': { weight: 144.24, name: 'Neodymium' },
    'Pm': { weight: 145, name: 'Promethium' },
    'Sm': { weight: 150.36, name: 'Samarium' },
    'Eu': { weight: 151.96, name: 'Europium' },
    'Gd': { weight: 157.25, name: 'Gadolinium' },
    'Tb': { weight: 158.93, name: 'Terbium' },
    'Dy': { weight: 162.5, name: 'Dysprosium' },
    'Ho': { weight: 164.93, name: 'Holmium' },
    'Er': { weight: 167.26, name: 'Erbium' },
    'Tm': { weight: 168.93, name: 'Thulium' },
    'Yb': { weight: 173.05, name: 'Ytterbium' },
    'Lu': { weight: 174.97, name: 'Lutetium' },
    'Hf': { weight: 178.49, name: 'Hafnium' },
    'Ta': { weight: 180.95, name: 'Tantalum' },
    'W': { weight: 183.84, name: 'Tungsten' },
    'Re': { weight: 186.21, name: 'Rhenium' },
    'Os': { weight: 190.23, name: 'Osmium' },
    'Ir': { weight: 192.22, name: 'Iridium' },
    'Pt': { weight: 195.08, name: 'Platinum' },
    'Au': { weight: 196.97, name: 'Gold' },
    'Hg': { weight: 200.59, name: 'Mercury' },
    'Tl': { weight: 204.38, name: 'Thallium' },
    'Pb': { weight: 207.2, name: 'Lead' },
    'Bi': { weight: 208.98, name: 'Bismuth' },
    'Po': { weight: 209, name: 'Polonium' },
    'At': { weight: 210, name: 'Astatine' },
    'Rn': { weight: 222, name: 'Radon' },
    'Fr': { weight: 223, name: 'Francium' },
    'Ra': { weight: 226, name: 'Radium' },
    'Ac': { weight: 227, name: 'Actinium' },
    'Th': { weight: 232.04, name: 'Thorium' },
    'Pa': { weight: 231.04, name: 'Protactinium' },
    'U': { weight: 238.03, name: 'Uranium' }
  };

  // Common compounds
  const commonCompounds = [
    { value: '', label: t('selectCompound') },
    { value: 'H2O', label: 'H2O (Water)' },
    { value: 'CO2', label: 'CO2 (Carbon Dioxide)' },
    { value: 'NH3', label: 'NH3 (Ammonia)' },
    { value: 'NaCl', label: 'NaCl (Sodium Chloride)' },
    { value: 'H2SO4', label: 'H2SO4 (Sulfuric Acid)' },
    { value: 'HCl', label: 'HCl (Hydrochloric Acid)' },
    { value: 'HNO3', label: 'HNO3 (Nitric Acid)' },
    { value: 'NaHCO3', label: 'NaHCO3 (Sodium Bicarbonate)' },
    { value: 'CaCO3', label: 'CaCO3 (Calcium Carbonate)' },
    { value: 'KCl', label: 'KCl (Potassium Chloride)' },
    { value: 'KMnO4', label: 'KMnO4 (Potassium Permanganate)' },
    { value: 'CuSO4.5H2O', label: 'CuSO4.5H2O (Copper(II) Sulfate Pentahydrate)' },
    { value: 'CH4', label: 'CH4 (Methane)' },
    { value: 'C2H5OH', label: 'C2H5OH (Ethanol)' },
    { value: 'C2H4O2', label: 'C2H4O2 (Acetic Acid)' },
    { value: 'C6H12O6', label: 'C6H12O6 (Glucose)' },
    { value: 'C6H6', label: 'C6H6 (Benzene)' },
    { value: 'C8H18', label: 'C8H18 (Octane)' },
    { value: 'C12H22O11', label: 'C12H22O11 (Sucrose)' },
    { value: 'C9H8O4', label: 'C9H8O4 (Aspirin)' },
    { value: 'C6H8O7', label: 'C6H8O7 (Citric Acid)' },
    { value: 'CH4N2O', label: 'CH4N2O (Urea)' }
  ];

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Parse formula functions
  const parseSimpleFormula = (formulaStr: string): Record<string, number> => {
    let i = 0;
    const stack: Record<string, number>[] = [{}];

    while (i < formulaStr.length) {
      if (formulaStr[i] === '(' || formulaStr[i] === '[') {
        stack.push({});
        i++;
      } else if (formulaStr[i] === ')' || formulaStr[i] === ']') {
        i++;
        let num = '';
        while (i < formulaStr.length && /\d/.test(formulaStr[i])) {
          num += formulaStr[i];
          i++;
        }
        const multiplier = num ? parseInt(num) : 1;
        const group = stack.pop()!;
        const current = stack[stack.length - 1];
        for (const el in group) {
          current[el] = (current[el] || 0) + group[el] * multiplier;
        }
      } else if (/[A-Z]/.test(formulaStr[i])) {
        let element = formulaStr[i];
        i++;
        while (i < formulaStr.length && /[a-z]/.test(formulaStr[i])) {
          element += formulaStr[i];
          i++;
        }
        let num = '';
        while (i < formulaStr.length && /\d/.test(formulaStr[i])) {
          num += formulaStr[i];
          i++;
        }
        const count = num ? parseInt(num) : 1;
        const current = stack[stack.length - 1];
        current[element] = (current[element] || 0) + count;
      } else {
        i++;
      }
    }

    return stack[0];
  };

  const parseFormula = (formulaStr: string): Record<string, number> => {
    const elements: Record<string, number> = {};
    const parts = formulaStr.split('.');

    parts.forEach(part => {
      const parsed = parseSimpleFormula(part);
      for (const element in parsed) {
        elements[element] = (elements[element] || 0) + parsed[element];
      }
    });

    return elements;
  };

  const calculateMolecularWeight = (formulaStr: string) => {
    const elements = parseFormula(formulaStr);
    let totalWeight = 0;
    const composition: Array<{
      symbol: string;
      name: string;
      count: number;
      weight: number;
      percentage: string;
    }> = [];

    for (const element in elements) {
      if (!atomicWeights[element]) {
        throw new Error(`${t('unknownElement')}: ${element}`);
      }
      const weight = atomicWeights[element].weight * elements[element];
      totalWeight += weight;
      composition.push({
        symbol: element,
        name: atomicWeights[element].name,
        count: elements[element],
        weight: weight,
        percentage: '0'
      });
    }

    composition.forEach(item => {
      item.percentage = (item.weight / totalWeight * 100).toFixed(3);
    });

    return { totalWeight, composition };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const formulaToCalculate = formula.trim();

    if (!formulaToCalculate) {
      setError(t('enterFormula'));
      return;
    }

    try {
      const result = calculateMolecularWeight(formulaToCalculate);
      setResults(result);
    } catch (err: any) {
      setError(`${t('error')}: ${err.message}. ${t('checkFormula')}`);
      setResults(null);
    }
  };

  const handleClear = () => {
    setFormula('');
    setSelectedCompound('');
    setResults(null);
    setError('');
  };

  const handleCompoundSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCompound(value);
    if (value) {
      setFormula(value);
    }
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.4rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.8rem', fontSize: '1.05rem' },
    { padding: '0.875rem', fontSize: '1.1rem' }
  );

  const selectStyle = { ...inputStyle };

  const formHintStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.25rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.9rem 1.6rem', fontSize: '1.05rem' },
    { padding: '1rem 1.75rem', fontSize: '1.1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.9rem 1.6rem', fontSize: '1.05rem' },
    { padding: '1rem 1.75rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      border: '2px solid #fecaca',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      display: error ? 'block' : 'none'
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '0.95rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #2563eb',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem'
    },
    { padding: '1.25rem', flexDirection: 'column' as const, gap: '0.5rem', textAlign: 'center' as const },
    { padding: '1.4rem', flexDirection: 'row' as const },
    { padding: '1.5rem', flexDirection: 'row' as const }
  );

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600 },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1.25rem' },
    { padding: '1.4rem' },
    { padding: '1.5rem' }
  );

  const h4Style = getResponsiveStyle(
    { marginTop: 0, color: '#1e293b' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '1rem'
  };

  const thTdStyle = getResponsiveStyle(
    { textAlign: 'left' as const, borderBottom: '1px solid #e2e8f0' },
    { padding: '0.65rem', fontSize: '0.85rem' },
    { padding: '0.7rem', fontSize: '0.875rem' },
    { padding: '0.75rem', fontSize: '0.9rem' }
  );

  const thStyle = {
    ...thTdStyle,
    background: '#f8fafc',
    fontWeight: 600,
    color: '#475569'
  };

  const tdStyle = {
    ...thTdStyle,
    color: '#1e293b'
  };

  const elementSymbolStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem', lineHeight: 1.6 },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const referenceListStyle = {
    listStyle: 'none' as const,
    padding: 0,
    margin: 0
  };

  const referenceItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      border: '1px solid rgba(255, 200, 180, 0.15)',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      transition: 'all 0.3s ease'
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1.125rem' }
  );

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    display: 'block',
    marginBottom: '0.25rem'
  };

  const referenceUrlStyle = getResponsiveStyle(
    { color: colors.textSecondary, wordBreak: 'break-all' as const },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>⚗️ {t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="molecular-formula">
                {t('formulaLabel')}
              </label>
              <input
                type="text"
                id="molecular-formula"
                placeholder={t('formulaPlaceholder')}
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <div style={formHintStyle}>{t('formulaHint')}</div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="common-chemicals">
                {t('quickSelectLabel')}
              </label>
              <select
                id="common-chemicals"
                value={selectedCompound}
                onChange={handleCompoundSelect}
                style={selectStyle}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              >
                {commonCompounds.map((compound, index) => (
                  <option key={index} value={compound.value}>
                    {compound.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(37, 99, 235, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('calculateButton')}
              </button>
              <button
                type="button"
                onClick={handleClear}
                style={clearBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t('clearButton')}
              </button>
            </div>

            {error && (
              <div style={errorMessageStyle}>
                {error}
              </div>
            )}
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <span style={resultLabelStyle}>{t('molecularWeightLabel')}</span>
                <span style={resultValueStyle}>{results.totalWeight.toFixed(3)} g/mol</span>
              </div>

              <div style={resultCardStyle}>
                <h4 style={h4Style}>{t('compositionTitle')}</h4>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('elementColumn')}</th>
                      <th style={thStyle}>{t('symbolColumn')}</th>
                      <th style={thStyle}>{t('atomsColumn')}</th>
                      <th style={thStyle}>{t('massColumn')}</th>
                      <th style={thStyle}>{t('percentageColumn')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.composition.map((item, index) => (
                      <tr key={index}>
                        <td style={tdStyle}>{item.name}</td>
                        <td style={tdStyle}>
                          <span style={elementSymbolStyle}>{item.symbol}</span>
                        </td>
                        <td style={tdStyle}>{item.count}</td>
                        <td style={tdStyle}>{item.weight.toFixed(3)}</td>
                        <td style={tdStyle}>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}
          </li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
          <li style={listItemStyle}>{t('insight5')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('whatIsMolecularWeightTitle')}
        </h3>
        <p style={paragraphStyle}>{t('whatIsMolecularWeightDescription')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('calculationMethodTitle')}
        </h3>
        <p style={paragraphStyle}>{t('calculationMethodDescription')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('applicationsTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('application1')}</li>
          <li style={listItemStyle}>{t('application2')}</li>
          <li style={listItemStyle}>{t('application3')}</li>
          <li style={listItemStyle}>{t('application4')}</li>
          <li style={listItemStyle}>{t('application5')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq1Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq2Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq3Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq4Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem' }}>
          {t('faq5Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            <a
              href="https://www.em-grade.com/chemistry-definitions/molecular-weight-pm-mw-and-fw/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference1Title')}
            </a>
            <span style={referenceUrlStyle}>EM Grade</span>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.calculator.net/molecular-weight-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference2Title')}
            </a>
            <span style={referenceUrlStyle}>Calculator.net</span>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://en.wikipedia.org/wiki/Molar_mass"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference3Title')}
            </a>
            <span style={referenceUrlStyle}>Wikipedia</span>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://blog.orendatech.com/understanding-the-mole-molecular-weights-in-chemistry"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference4Title')}
            </a>
            <span style={referenceUrlStyle}>Orenda Tech Blog</span>
          </li>
        </ul>
      </section>
    </div>
  );
}